import { useEffect, type Dispatch, type SetStateAction } from 'react';
import L from 'leaflet';
import * as turf from '@turf/turf';
import type { GridParams } from 'contexts/GridContext';
import { lv95ToWgs84ForOneCoordinate } from '../map/coordinateTransforms';
import type { GridMapType } from '../map/mapConfig';
import { isWithinSwitzerland } from 'services/isWithinSwitzerland';
import {
  getLayerMap,
  getLayerOuterRing,
  layerRingToLv95,
  type BorderType,
  type EditLayerHandlers,
  type Lv95Coordinate,
  type SetGridParams,
  type TurfPolygon,
} from './cadastralShared';
import {
  buildTurfPolygonFromLatLngs,
  buildTurfPolygonFromLv95,
  getCombinedAnalysisAreasCenter,
} from './cadastralGeometry';
import type { AddCenterMarker } from './AnalysisAreaCenterMarkerSync';

interface CadastralBorderEditingOptions {
  activeEditBorderType: BorderType | null;
  setActiveEditBorderType: Dispatch<SetStateAction<BorderType | null>>;
  mapType: GridMapType;
  gridParams: GridParams;
  analysisAreas: Lv95Coordinate[][];
  activeAnalysisAreaIndex: number;
  coordinates: Lv95Coordinate[];
  setCoordinates: Dispatch<SetStateAction<Lv95Coordinate[]>>;
  setEditingPolygonType: Dispatch<SetStateAction<BorderType | null>>;
  setShowEditableCoordinatesArea: Dispatch<SetStateAction<boolean>>;
  activeEditBorderRef: { current: BorderType | null };
  editOriginalCoordinatesRef: { current: Partial<Record<BorderType, Lv95Coordinate[] | null>> };
  editDraftCoordinatesRef: { current: Partial<Record<BorderType, Lv95Coordinate[] | null>> };
  editKeydownHandlerRef: { current: Partial<Record<BorderType, (event: KeyboardEvent) => void>> };
  editLayerHandlersRef: { current: Partial<Record<BorderType, EditLayerHandlers>> };
  getLayerRefByType: (borderType: BorderType) => L.Polygon | null;
  getBorderCoordinatesByType: (borderType: BorderType) => Lv95Coordinate[];
  removeRotateControl: () => void;
  renderRotateControl: (borderType: BorderType) => void;
  updateRotateControlPosition: () => void;
  ensureRotateControl: (borderType: BorderType) => boolean;
  validatePolygonDoesNotSelfIntersect: (borderType: BorderType, polygon: TurfPolygon | null) => boolean;
  validateLegalBuildableContainment: (borderType: BorderType, polygon: TurfPolygon | null) => boolean;
  validateProjectRadiusRequirement: (borderType: BorderType, polygon: TurfPolygon | null) => boolean;
  validateAnalysisAreaSeparation: (
    borderType: BorderType,
    polygon: TurfPolygon | null,
    options: { ignoreIndex?: number }
  ) => boolean;
  syncActiveAnalysisAreaCoordinates: (
    coordinates: Lv95Coordinate[],
    options: { analysisAreaType?: string; recenter?: boolean }
  ) => void;
  syncNonAnalysisBorder: (borderType: Exclude<BorderType, 'analysisArea'>, coordinates: Lv95Coordinate[]) => void;
  addCenterMarker: AddCenterMarker;
  setGridParams: SetGridParams;
  onOutsideSwitzerland: () => void;
}

const useCadastralBorderEditing = ({
  activeEditBorderType,
  setActiveEditBorderType,
  mapType,
  gridParams,
  analysisAreas,
  activeAnalysisAreaIndex,
  coordinates,
  setCoordinates,
  setEditingPolygonType,
  setShowEditableCoordinatesArea,
  activeEditBorderRef,
  editOriginalCoordinatesRef,
  editDraftCoordinatesRef,
  editKeydownHandlerRef,
  editLayerHandlersRef,
  getLayerRefByType,
  getBorderCoordinatesByType,
  removeRotateControl,
  renderRotateControl,
  updateRotateControlPosition,
  ensureRotateControl,
  validatePolygonDoesNotSelfIntersect,
  validateLegalBuildableContainment,
  validateProjectRadiusRequirement,
  validateAnalysisAreaSeparation,
  syncActiveAnalysisAreaCoordinates,
  syncNonAnalysisBorder,
  addCenterMarker,
  setGridParams,
  onOutsideSwitzerland,
}: CadastralBorderEditingOptions) => {
  const snapshotEditState = (borderType: BorderType) => {
    const originalCoordinates = getBorderCoordinatesByType(borderType);
    editOriginalCoordinatesRef.current[borderType] = originalCoordinates;
    editDraftCoordinatesRef.current[borderType] = originalCoordinates;
  };

  const disableEditMode = (borderType: BorderType) => {
    const layerRef = getLayerRefByType(borderType);
    if (layerRef?.pm?.enabled?.()) layerRef.pm.disable();
    if (editKeydownHandlerRef.current[borderType]) {
      document.removeEventListener('keydown', editKeydownHandlerRef.current[borderType]);
      delete editKeydownHandlerRef.current[borderType];
    }
    removeRotateControl();
    activeEditBorderRef.current = null;
    setActiveEditBorderType(null);
    setEditingPolygonType(null);
  };

  useEffect(() => {
    if (!activeEditBorderType) return;
    renderRotateControl(activeEditBorderType);
  }, [activeEditBorderType, renderRotateControl]);

  useEffect(() => {
    if (!activeEditBorderType) return;
    const layerRef = getLayerRefByType(activeEditBorderType);
    const map = getLayerMap(layerRef);
    if (!layerRef || !map) return;

    const handleZoomEnd = () => updateRotateControlPosition();
    map.on('zoomend', handleZoomEnd);
    return () => {
      map.off('zoomend', handleZoomEnd);
    };
  }, [activeEditBorderType, updateRotateControlPosition]);

  useEffect(() => {
    if (!activeEditBorderType) return;
    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    const borderType = activeEditBorderType;
    const draftCoordinates = editDraftCoordinatesRef.current[borderType];
    const coordinatesToRestore = draftCoordinates?.length ? draftCoordinates : getBorderCoordinatesByType(borderType);

    const restoreEditMode = () => {
      if (cancelled) return;
      const layerRef = getLayerRefByType(borderType);
      if (!layerRef || !getLayerMap(layerRef)) {
        retryTimer = setTimeout(restoreEditMode, 50);
        return;
      }
      if (coordinatesToRestore?.length) {
        const wgs84Coordinates = coordinatesToRestore.map((coordinate) =>
          lv95ToWgs84ForOneCoordinate(coordinate.x, coordinate.y)
        );
        layerRef.setLatLngs(wgs84Coordinates);
        setEditingPolygonType(borderType);
        setCoordinates(coordinatesToRestore);
      }
      setShowEditableCoordinatesArea(true);
      enableEditMode(borderType);
      ensureRotateControl(borderType);
    };

    restoreEditMode();
    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [activeEditBorderType, ensureRotateControl, mapType]);

  const saveEditForBorder = (borderType: BorderType) => {
    const layerRef = getLayerRefByType(borderType);
    if (!layerRef) return;
    const finalLv95Coordinates = coordinates;
    const editedPolygon =
      buildTurfPolygonFromLatLngs(getLayerOuterRing(layerRef)) || buildTurfPolygonFromLv95(finalLv95Coordinates);
    if (!validatePolygonDoesNotSelfIntersect(borderType, editedPolygon)) {
      cancelEditForBorder(borderType);
      return;
    }
    if (!validateLegalBuildableContainment(borderType, editedPolygon)) {
      cancelEditForBorder(borderType);
      return;
    }
    if (!validateProjectRadiusRequirement(borderType, editedPolygon)) {
      cancelEditForBorder(borderType);
      return;
    }
    if (!validateAnalysisAreaSeparation(borderType, editedPolygon, { ignoreIndex: activeAnalysisAreaIndex })) {
      cancelEditForBorder(borderType);
      return;
    }
    if (!isWithinSwitzerland(finalLv95Coordinates, gridParams.analysisAreaType)) {
      onOutsideSwitzerland();
      cancelEditForBorder(borderType);
      return;
    }

    const analysisAreaCenterSyncData =
      borderType === 'analysisArea'
        ? (() => {
            const latLngs = getLayerOuterRing(layerRef);
            const layerMap = getLayerMap(layerRef);
            if (latLngs.length <= 2 || !layerMap) return null;
            const polygonCoordinates = latLngs.map((coordinate) => [coordinate.lng, coordinate.lat]);
            polygonCoordinates.push(polygonCoordinates[0]);
            return { polygon: turf.polygon([polygonCoordinates]), map: layerMap };
          })()
        : null;

    disableEditMode(borderType);
    editOriginalCoordinatesRef.current[borderType] = null;
    editDraftCoordinatesRef.current[borderType] = null;

    if (borderType === 'analysisArea') {
      syncActiveAnalysisAreaCoordinates(finalLv95Coordinates, {
        analysisAreaType:
          analysisAreas.length > 1 ? 'newAnalysisArea' : gridParams.analysisAreaType || 'newAnalysisArea',
        recenter: true,
      });
    } else {
      syncNonAnalysisBorder(borderType, finalLv95Coordinates);
    }
    setCoordinates(finalLv95Coordinates);
    if (analysisAreaCenterSyncData) {
      const nextAnalysisAreas = analysisAreas.map((polygon, index) =>
        index === activeAnalysisAreaIndex ? finalLv95Coordinates : polygon
      );
      const combinedCenter = getCombinedAnalysisAreasCenter(nextAnalysisAreas);
      addCenterMarker(
        analysisAreaCenterSyncData.polygon,
        analysisAreaCenterSyncData.map,
        setGridParams,
        combinedCenter ? [combinedCenter.x, combinedCenter.y] : null
      );
    }
  };

  const cancelEditForBorder = (borderType: BorderType) => {
    const originalCoordinates = editOriginalCoordinatesRef.current[borderType];
    if (originalCoordinates?.length) {
      setEditingPolygonType(borderType);
      setCoordinates(originalCoordinates);
      const layerRef = getLayerRefByType(borderType);
      if (layerRef) {
        const wgs84Coordinates = originalCoordinates.map((coordinate) =>
          lv95ToWgs84ForOneCoordinate(coordinate.x, coordinate.y)
        );
        layerRef.setLatLngs(wgs84Coordinates);
      }
    }
    disableEditMode(borderType);
    editOriginalCoordinatesRef.current[borderType] = null;
    editDraftCoordinatesRef.current[borderType] = null;
  };

  const enableEditMode = (borderType: BorderType) => {
    const layerRef = getLayerRefByType(borderType);
    if (!layerRef) return;
    layerRef.pm.enable();
    activeEditBorderRef.current = borderType;
    setActiveEditBorderType(borderType);

    const existingHandlers = editLayerHandlersRef.current[borderType];
    if (existingHandlers) {
      layerRef.off('pm:edit', existingHandlers.onEdit);
      layerRef.off('pm:vertexremoved', existingHandlers.onVertexRemoved);
    }
    const onEdit: EditLayerHandlers['onEdit'] = (event) => {
      const updatedCoordinates = layerRingToLv95(event.layer);
      editDraftCoordinatesRef.current[borderType] = updatedCoordinates;
      setCoordinates(updatedCoordinates);
      updateRotateControlPosition();
    };
    const onVertexRemoved: EditLayerHandlers['onVertexRemoved'] = (event) => {
      const coordinate = (event as unknown as { vertex: L.Marker }).vertex.getLatLng();
      (event.layer as L.Polygon).addLatLng(coordinate);
      const updatedCoordinates = layerRingToLv95(event.layer);
      editDraftCoordinatesRef.current[borderType] = updatedCoordinates;
      setCoordinates(updatedCoordinates);
      updateRotateControlPosition();
    };
    editLayerHandlersRef.current[borderType] = { onEdit, onVertexRemoved };
    layerRef.on('pm:edit', onEdit);
    layerRef.on('pm:vertexremoved', onVertexRemoved);

    const handleEscKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'Escape') cancelEditForBorder(borderType);
    };
    if (editKeydownHandlerRef.current[borderType]) {
      document.removeEventListener('keydown', editKeydownHandlerRef.current[borderType]);
    }
    editKeydownHandlerRef.current[borderType] = handleEscKeyPress;
    document.addEventListener('keydown', handleEscKeyPress);
    ensureRotateControl(borderType);
  };

  return { snapshotEditState, disableEditMode, saveEditForBorder, cancelEditForBorder, enableEditMode };
};

export default useCadastralBorderEditing;
